import { useEffect, useId, useLayoutEffect, useRef, useState } from "react"

// Animated mini-mockup for the Analyse feature row.
// 18s loop: empty → click → 5 thinking steps → 5 tabs of extracted data cycle through.
// Styles live under `.al-*` classes in src/index.css (scoped to .landing-page).

export function AnalyseVisual() {
  const rootRef = useRef<HTMLDivElement>(null)
  const stageRef = useRef<HTMLDivElement>(null)
  const ctaRef = useRef<HTMLButtonElement>(null)
  const [inView, setInView] = useState(false)
  const gradientId = useId()

  useEffect(() => {
    const el = rootRef.current
    if (!el) return
    const obs = new IntersectionObserver(
      (entries) => setInView(entries[0]?.isIntersecting ?? false),
      { threshold: 0.4 },
    )
    obs.observe(el)
    return () => obs.disconnect()
  }, [])

  // Compute the cursor target (CTA centre) and start position (off-frame right).
  // CSS keyframes consume these via custom properties.
  //
  // We use offsetLeft/offsetWidth (the layout box) rather than
  // getBoundingClientRect (the rendered box) so the values are in the
  // visual's unscaled 640px design space. The CSS `translate(...px)` in
  // al-cursor-path runs in that same unscaled space; once we set the
  // vars correctly, the parent transform: scale() on mobile takes the
  // cursor and the CTA down together.
  useLayoutEffect(() => {
    const update = () => {
      const stage = stageRef.current
      const cta = ctaRef.current
      const root = rootRef.current
      if (!stage || !cta || !root) return
      // Walk offsetParent chain up to (and excluding) the stage so we get
      // the CTA's position relative to the stage in unscaled CSS pixels.
      let x = 0
      let y = 0
      let el: HTMLElement | null = cta
      while (el && el !== stage) {
        x += el.offsetLeft
        y += el.offsetTop
        el = el.offsetParent as HTMLElement | null
      }
      const tipOffsetX = 4
      const tipOffsetY = 3
      const targetX = x + cta.offsetWidth / 2 - tipOffsetX
      const targetY = y + cta.offsetHeight / 2 - tipOffsetY
      const startX = stage.offsetWidth + 60
      const startY = targetY - 10
      root.style.setProperty("--al-cursor-target-x", `${targetX}px`)
      root.style.setProperty("--al-cursor-target-y", `${targetY}px`)
      root.style.setProperty("--al-cursor-start-x", `${startX}px`)
      root.style.setProperty("--al-cursor-start-y", `${startY}px`)
    }
    update()
    window.addEventListener("resize", update)
    return () => window.removeEventListener("resize", update)
  }, [])

  return (
    <div ref={rootRef} className={`al-frame ${inView ? "al-playing" : ""}`}>
      {/* ── document tile ── */}
      <div className="al-doc-tile">
        <div className="al-doc-icon"><span>PDF</span></div>
        <div className="al-doc-meta">
          <span className="al-doc-name">Westbrook_Tender_Pack.pdf</span>
          <span className="al-doc-sub">412 pages · 18.4 MB · uploaded just now</span>
        </div>
      </div>

      <div ref={stageRef} className="al-stage">
        {/* Phase 1: Empty + CTA */}
        <div className="al-phase al-phase-empty">
          <p className="al-empty-msg">
            Tender ready to analyse. We&apos;ll extract criteria, requirements, dates, and risks.
          </p>
          <button ref={ctaRef} type="button" className="al-cta">
            <span className="al-cta-label-default">
              <svg
                className="al-cta-icon"
                viewBox="0 0 24 24"
                fill="none"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <defs>
                  <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="hsl(88 60% 72%)" />
                    <stop offset="100%" stopColor="hsl(258 95% 78%)" />
                  </linearGradient>
                </defs>
                <path
                  stroke={`url(#${gradientId})`}
                  d="M12 2 L13.8 10.2 L22 12 L13.8 13.8 L12 22 L10.2 13.8 L2 12 L10.2 10.2 Z"
                />
              </svg>
              Analyse tender
            </span>
            <span className="al-cta-label-loading">
              <span className="al-cta-spinner" />
              Analysing…
            </span>
          </button>
        </div>

        {/* Phase 2: Steps */}
        <div className="al-phase al-phase-steps">
          {[
            "Reading the tender pack",
            "Identifying evaluation criteria",
            "Mapping compliance requirements",
            "Extracting key dates",
            "Assessing risks",
          ].map((label, i) => (
            <div key={label} className={`al-step al-s${i + 1}`}>
              <span className="al-dot">
                <span className="al-ring" />
                <span className="al-check" />
              </span>
              <span className="al-label">{label}</span>
            </div>
          ))}
        </div>

        {/* Phase 3: Result (tabs + 5 tab contents) */}
        <div className="al-phase al-phase-result">
          <div className="al-tabs">
            <div className="al-tab al-t1">Criteria <span className="al-count al-c1" /></div>
            <div className="al-tab al-t2">Compliance <span className="al-count al-c2" /></div>
            <div className="al-tab al-t3">Checklist <span className="al-count al-c3" /></div>
            <div className="al-tab al-t4">Dates <span className="al-count al-c4" /></div>
            <div className="al-tab al-t5">Risks <span className="al-count al-c5" /></div>
          </div>

          <div className="al-tab-contents">
            {/* tc1: Criteria */}
            <div className="al-tc al-tc1">
              <table className="al-table">
                <thead>
                  <tr>
                    <th style={{ width: "48%" }}>Criterion</th>
                    <th style={{ width: "18%" }}>Weight</th>
                    <th style={{ width: "18%" }}>Section</th>
                    <th style={{ width: "16%" }}>Source</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="al-r1"><td>Technical Capacity & Methodology</td><td><span className="al-weight-pill">30%</span></td><td className="al-muted">Section 4.2</td><td className="al-muted">p. 47</td></tr>
                  <tr className="al-r2"><td>Past Project Experience</td><td><span className="al-weight-pill">25%</span></td><td className="al-muted">Section 4.3</td><td className="al-muted">p. 53</td></tr>
                  <tr className="al-r3"><td>Project Management Approach</td><td><span className="al-weight-pill">20%</span></td><td className="al-muted">Section 4.4</td><td className="al-muted">p. 61</td></tr>
                  <tr className="al-skel al-skel-1"><td><span className="al-skel-bar al-w-70" /></td><td><span className="al-skel-bar al-w-30" /></td><td><span className="al-skel-bar al-w-50" /></td><td><span className="al-skel-bar al-w-30" /></td></tr>
                  <tr className="al-skel al-skel-2"><td><span className="al-skel-bar al-w-60" /></td><td><span className="al-skel-bar al-w-30" /></td><td><span className="al-skel-bar al-w-50" /></td><td><span className="al-skel-bar al-w-30" /></td></tr>
                </tbody>
              </table>
            </div>

            {/* tc2: Compliance */}
            <div className="al-tc al-tc2">
              <table className="al-table">
                <thead>
                  <tr>
                    <th style={{ width: "42%" }}>Requirement</th>
                    <th style={{ width: "20%" }}>Category</th>
                    <th style={{ width: "18%" }}>Clause</th>
                    <th style={{ width: "20%" }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="al-r1"><td>ISO 45001 certification</td><td className="al-muted">Safety</td><td className="al-muted">Cl. 7.2</td><td><span className="al-status-pill al-met">✓ Met</span></td></tr>
                  <tr className="al-r2"><td>$20M Professional Indemnity</td><td className="al-muted">Insurance</td><td className="al-muted">Cl. 9.1</td><td><span className="al-status-pill al-review">⚠ Review</span></td></tr>
                  <tr className="al-r3"><td>Local supplier registry</td><td className="al-muted">Procurement</td><td className="al-muted">Cl. 5.4</td><td><span className="al-status-pill al-met">✓ Met</span></td></tr>
                  <tr className="al-skel al-skel-1"><td><span className="al-skel-bar al-w-60" /></td><td><span className="al-skel-bar al-w-40" /></td><td><span className="al-skel-bar al-w-50" /></td><td><span className="al-skel-bar al-w-50" /></td></tr>
                  <tr className="al-skel al-skel-2"><td><span className="al-skel-bar al-w-70" /></td><td><span className="al-skel-bar al-w-40" /></td><td><span className="al-skel-bar al-w-50" /></td><td><span className="al-skel-bar al-w-50" /></td></tr>
                </tbody>
              </table>
            </div>

            {/* tc3: Checklist */}
            <div className="al-tc al-tc3">
              <table className="al-table">
                <thead>
                  <tr>
                    <th style={{ width: "42%" }}>Item</th>
                    <th style={{ width: "18%" }}>Format</th>
                    <th style={{ width: "18%" }}>Limit</th>
                    <th style={{ width: "22%" }}>Method</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="al-r1"><td>Technical proposal</td><td className="al-muted">PDF</td><td className="al-muted">50 pages</td><td className="al-muted">Online portal</td></tr>
                  <tr className="al-r2"><td>Key personnel CVs</td><td className="al-muted">Word</td><td className="al-muted">Max 4</td><td className="al-muted">Online portal</td></tr>
                  <tr className="al-r3"><td>Methodology statement</td><td className="al-muted">PDF</td><td className="al-muted">25 pages</td><td className="al-muted">Online portal</td></tr>
                  <tr className="al-skel al-skel-1"><td><span className="al-skel-bar al-w-60" /></td><td><span className="al-skel-bar al-w-40" /></td><td><span className="al-skel-bar al-w-40" /></td><td><span className="al-skel-bar al-w-50" /></td></tr>
                  <tr className="al-skel al-skel-2"><td><span className="al-skel-bar al-w-70" /></td><td><span className="al-skel-bar al-w-40" /></td><td><span className="al-skel-bar al-w-40" /></td><td><span className="al-skel-bar al-w-50" /></td></tr>
                </tbody>
              </table>
            </div>

            {/* tc4: Dates */}
            <div className="al-tc al-tc4">
              <table className="al-table">
                <thead>
                  <tr>
                    <th style={{ width: "42%" }}>Event</th>
                    <th style={{ width: "18%" }}>Date</th>
                    <th style={{ width: "18%" }}>Time</th>
                    <th style={{ width: "22%" }}>Countdown</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="al-r1"><td>Q&amp;A submission</td><td className="al-muted">14 May</td><td className="al-muted">17:00</td><td><span className="al-countdown-pill al-urgent">9 days</span></td></tr>
                  <tr className="al-r2"><td>Tender submission</td><td className="al-muted">12 Jun</td><td className="al-muted">12:00</td><td><span className="al-countdown-pill">38 days</span></td></tr>
                  <tr className="al-r3"><td>Contract award</td><td className="al-muted">30 Jun</td><td className="al-muted">—</td><td><span className="al-countdown-pill">56 days</span></td></tr>
                  <tr className="al-skel al-skel-1"><td><span className="al-skel-bar al-w-60" /></td><td><span className="al-skel-bar al-w-40" /></td><td><span className="al-skel-bar al-w-30" /></td><td><span className="al-skel-bar al-w-40" /></td></tr>
                  <tr className="al-skel al-skel-2"><td><span className="al-skel-bar al-w-70" /></td><td><span className="al-skel-bar al-w-40" /></td><td><span className="al-skel-bar al-w-30" /></td><td><span className="al-skel-bar al-w-40" /></td></tr>
                </tbody>
              </table>
            </div>

            {/* tc5: Risks */}
            <div className="al-tc al-tc5">
              <table className="al-table">
                <thead>
                  <tr>
                    <th style={{ width: "34%" }}>Risk</th>
                    <th style={{ width: "16%" }}>Severity</th>
                    <th style={{ width: "14%" }}>Clause</th>
                    <th style={{ width: "36%" }}>Mitigation</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="al-r1"><td>Late mobilisation</td><td><span className="al-severity-pill al-high">High</span></td><td className="al-muted">§3.2</td><td className="al-muted">Pre-order long-leads</td></tr>
                  <tr className="al-r2"><td>Weather delays</td><td><span className="al-severity-pill al-med">Medium</span></td><td className="al-muted">§6.1</td><td className="al-muted">Float in schedule</td></tr>
                  <tr className="al-r3"><td>Subcontractor capacity</td><td><span className="al-severity-pill al-med">Medium</span></td><td className="al-muted">§4.7</td><td className="al-muted">Backup vendors</td></tr>
                  <tr className="al-skel al-skel-1"><td><span className="al-skel-bar al-w-60" /></td><td><span className="al-skel-bar al-w-30" /></td><td><span className="al-skel-bar al-w-30" /></td><td><span className="al-skel-bar al-w-60" /></td></tr>
                  <tr className="al-skel al-skel-2"><td><span className="al-skel-bar al-w-70" /></td><td><span className="al-skel-bar al-w-30" /></td><td><span className="al-skel-bar al-w-30" /></td><td><span className="al-skel-bar al-w-60" /></td></tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Cursor + ripple */}
        <div className="al-cursor">
          <svg viewBox="0 0 24 24">
            <path
              d="M5 3 L5 19 L9 15 L11 21 L13.5 20 L11.5 14 L17 14 Z"
              fill="white"
              stroke="#0F1419"
              strokeWidth="1.4"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <div className="al-ripple" />
      </div>
    </div>
  )
}
